import { CanvasEngineProperties } from './properties'
import type {
  CanvasEngineConstructor,
  CanvasEngineSoftLayer,
  ICanvasEngineLayerController,
} from './types'
import { getRandomString } from '@/lib/Functions'
import { FileToBase64 } from './helper'
import { SHAPES } from './const'
import { CanvasEngineEntity } from './entity'
import type { IImage, ILayer } from './backend/IRenderer'
import { TileLayer } from './tileLayer'
import { Floorplan } from '@/app/data/type'

const backgroundLayerId = '##background'
const defaultLayerId = '##default'

export class CanvasEngineLayerController
  extends CanvasEngineProperties
  implements ICanvasEngineLayerController
{
  // 핀치 줌 상태 (handleController에서 설정, layerController에서 참조)

  private viewportChangeTimeout: ReturnType<typeof setTimeout> | null = null // 디바운스를 위한 타이머 ID 저장용

  constructor(arg: CanvasEngineConstructor) {
    super(arg)
  }

  getDefaultLayerId = (): string => {
    return this.softLayerNameMap[defaultLayerId] ?? ''
  }

  getBackgroundLayerId = (): string => {
    return this.softLayerNameMap[backgroundLayerId] ?? ''
  }

  getBackgroundLayer = (): ILayer => {
    return this.renderer.layers.background
  }

  /** 소프트 레이어 id → 이름 (경계 API — 바깥에서 Konva 레이어 name()을 읽지 않도록). 없는 id면 undefined */
  getLayerName = (layerId: string): string | undefined => {
    return this.softLayerNameMap[layerId]
  }

  selectLayer = (layerId: string): void => {
    this.selectedLayerId = layerId
    this.events.onSelectLayer(this.selectedLayerId)
  }

  createLayer = (name: string, id: string = getRandomString(12, false)): void => {
    if (name == 'default-layer') {
      this.defaultLayerId = id
    }
    this.entityLayerMap[id] = []
    this.softLayerNameMap[id] = name

    if (name == 'default-layer') {
      this.selectLayer(id)
    }
    this.events.onCreateLayer({ layerId: id, layerName: name })
    this.events.onUpdateLayer(
      Object.entries(this.softLayerNameMap).map(([layerId, layerName]: string[]) => ({
        layerId,
        layerName,
      })),
    )
    this.onLayerUpdate()
  }

  renameLayer = (layerId: string, name: string): void => {
    const layer = this.softLayerNameMap[layerId]
    if (!layer) {
      throw new Error('레이어를 찾을 수 없습니다.')
    }
    this.onLayerUpdate()
    this.softLayerNameMap[layerId] = name
  }

  removeLayer = (layerId: string = this.selectedLayerId): string => {
    const layer = this.softLayerNameMap[layerId]
    if (!layer) {
      throw new Error('레이어를 찾을 수 없습니다.')
    }
    while (this.entityLayerMap[layerId].length > 0) {
      const shape = this.entityLayerMap[layerId].pop()
      if (shape) {
        this.entityLayerMap[this.defaultLayerId].push(shape)
      }
    }

    delete this.entityLayerMap[layerId]
    delete this.softLayerNameMap[layerId]

    if (layerId == this.selectedLayerId) {
      this.selectLayer(this.getDefaultLayerId()!)
    }
    this.events.onDeleteLayer(layerId)
    this.onLayerUpdate()
    return ''
  }

  hasLayer = (layerId: string): boolean => {
    return this.entityLayerMap[layerId] != undefined
  }

  showLayer = (layerId: string = this.selectedLayerId, serverLayerId: string = ''): void => {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (
        (entity.getLayerId() == layerId || entity.getLayerId() == serverLayerId) &&
        entity.entityType != SHAPES.BRUSH
      ) {
        entity.node.show()
      }
    })
    this.onLayerUpdate()
  }
  hideLayer = (layerId: string = this.selectedLayerId, serverLayerId: string = ''): void => {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (
        (entity.getLayerId() == layerId || entity.getLayerId() == serverLayerId) &&
        entity.entityType != SHAPES.BRUSH
      ) {
        entity.node.hide()
      }
    })
    this.onLayerUpdate()
  }

  showBrushShapes = (): void => {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (entity.entityType == SHAPES.BRUSH) {
        entity.node.show()
      }
    })
    this.onLayerUpdate()
  }

  hideBrushShapes = (): void => {
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (entity.entityType == SHAPES.BRUSH) {
        entity.node.hide()
      }
    })
    this.onLayerUpdate()
  }

  /**
   * @param beforeDraw 새 배경 크기가 정해진 뒤, 첫 그리기 직전에 호출 — 호출자가 새 층 뷰포트를 먼저 맞출 자리.
   *   이전 층 배율로 큰 배경을 먼저 그리면 GPU 디코드 캐시가 그 배율용(mip) 디코드를 메인 스레드에서 한 번 더 한다
   */
  async setBackground(
    data: File | string,
    rotate: number = 0,
    options?: Pick<Floorplan, 'is_tiled' | 'tile_config' | 'tile_base_url'>,
    beforeDraw?: () => void,
  ): Promise<void> {
    if (!data) return

    // 기존 타일 레이어가 있으면 정리
    if (this.tileLayer) {
      this.tileLayer.destroy()
      this.tileLayer = undefined
      this.renderer.stage.off('dragend.tileEvents wheel.tileEvents')
    }

    const imageUrl = data instanceof File ? await FileToBase64(data) : data
    const isTiled = !!(options?.is_tiled && options.tile_config && options.tile_base_url)

    let background: IImage
    try {
      // 큰 배경의 디코드를 오프스레드로 미리 끝낸다(첫 그리기의 메인 스레드 동기 디코드 ≈0.5초 제거)
      background = await this.renderer.loadImage(imageUrl, {}, true)
    } catch (error) {
      throw new Error(typeof error === 'string' ? error : 'Failed to load background image')
    }
    background.x(0)
    background.y(0)
    background.draggable(false)
    background.listening(false)

    // 먼저 이미지 크기 저장 (타일 모드일 경우 나중에 덮어씀)
    this.backgroundWidth = background.width()
    this.backgroundHeight = background.height()
    this.averageBackgroundSize = (this.backgroundWidth + this.backgroundHeight) / 2

    const backgroundLayer = this.renderer.layers.background
    backgroundLayer.add(background)
    // 기존 배경 이미지 제거
    this.destroyBackgroundImage()
    this.backgroundImage = background

    // 회전 처리
    this.backgroundImage.rotate(rotate)
    this.backgroundRotation = rotate as 0 | 90 | 180 | 270
    if (rotate == 90) {
      background.x(this.backgroundHeight)
      background.y(0)
    } else if (rotate == 180) {
      background.x(this.backgroundWidth)
      background.y(this.backgroundHeight)
    } else if (rotate == 270) {
      background.x(0)
      background.y(this.backgroundWidth)
    }
    background.moveToBottom()

    if (isTiled) {
      // 타일인 경우 — 크기는 타일 기준이므로 첫 그리기(beforeDraw) 전에 정한다
      try {
        // 타일 레이어 생성
        this.tileLayer = new TileLayer(
          {
            tile_config: options!.tile_config,
            tile_base_url: options!.tile_base_url,
          },
          this.renderer,
          this.backgroundImage?.getAbsoluteRotation(),
        )

        const { width, height } = this.tileLayer.getSize()
        this.backgroundWidth = width
        this.backgroundHeight = height
        this.averageBackgroundSize = (this.backgroundWidth + this.backgroundHeight) / 2
      } catch (error) {
        // 실패시 일반 이미지만 나옴
        console.error('타일 레이어 생성 실패:', error)
      }
    }

    beforeDraw?.()
    this.renderer.drawNow(backgroundLayer)

    if (this.tileLayer) {
      try {
        // 초기 뷰포트의 타일 로드
        await this.tileLayer.loadVisibleTiles(this.getTileViewport(), this.renderer.stage.scaleX())

        // 스테이지 이동/줌 이벤트에 타일 로드 연결
        this.initializeTileEvents()
      } catch (error) {
        console.error('타일 레이어 생성 실패:', error)
      }
    }

    // 모든 엔티티의 cloud border 재생성
    this.events.onBackgroundSizeChanged(this.backgroundWidth, this.backgroundHeight)
    this.entities.forEach((entity: CanvasEngineEntity) => {
      if (entity.onBackgroundSizeChanged) {
        entity.onBackgroundSizeChanged()
      }
    })
  }

  // 현재 뷰포트(월드 좌표)
  private getTileViewport(): { x: number; y: number; width: number; height: number } {
    const stage = this.renderer.stage
    const zoom = stage.scaleX()
    return {
      x: -stage.x() / zoom,
      y: -stage.y() / zoom,
      width: stage.width() / zoom,
      height: stage.height() / zoom,
    }
  }

  initializeTileEvents(): void {
    if (!this.stage) return
    // 타일 훅은 스테이지 원시 이벤트에 그대로 건다(fitView 뒤 미갱신은 기존 현상)
    const stage = this.renderer.stage
    // 기존 이벤트 제거
    stage.off('.tileEvents')
    // 화면 이동
    stage.on('dragend.tileEvents', () => this.handleViewportChange())
    // 마우스 휠 줌
    stage.on('wheel.tileEvents', () => this.handleViewportChange())
    // 모바일 핀치줌
    stage.on('touchend.tileEvents', () => {
      // 약간의 지연을 주어 isPinchZooming 상태가 업데이트되도록 함
      setTimeout(() => {
        if (!this.isPinchZooming) this.handleViewportChange()
      }, 50)
    })
  }
  // 타일 뷰포트 변경 처리
  handleViewportChange(): void {
    if (!this.tileLayer) return

    // 핀치 줌 중에는 타일 로드 스킵
    if (this.isPinchZooming) return

    // 디바운스
    if (this.viewportChangeTimeout) clearTimeout(this.viewportChangeTimeout)
    this.viewportChangeTimeout = setTimeout(() => {
      this.tileLayer?.loadVisibleTiles(this.getTileViewport(), this.renderer.stage.scaleX())
    }, 80)
  }

  getLayers = (): CanvasEngineSoftLayer[] => {
    return Object.entries(this.softLayerNameMap).map(([layerId, layerName]: string[]) => ({
      layerId,
      layerName,
    }))
  }

  /**
   * 선택된 레이어를 반환합니다.
   */
  getSelectedLayer = (): CanvasEngineSoftLayer & { name: string; id: string } => {
    return {
      id: this.selectedLayerId,
      layerId: this.selectedLayerId,
      name: this.softLayerNameMap[this.selectedLayerId],
      layerName: this.softLayerNameMap[this.selectedLayerId],
    }
  }

  getSystemLayer = (): ILayer => {
    return this.renderer.layers.system
  }

  importLayers = (layers: { id: string; name: string }[]): void => {
    for (const layer of layers) {
      this.importLayer(layer.name, layer.id)
    }
  }
  importLayer = (layerId: string, name: string): void => {
    this.createLayer(name, layerId)
  }

  async resetAllLayer(): Promise<void> {
    this.index = 1
    // 제스처 중 층 전환 등으로 브러시가 임시 그룹에 있으면 먼저 원복 (유령 비트맵 방지)
    this.endGestureBrushCache()
    this.destroyBrushSnapshot()

    this.entityLayerMap = {}
    this.softLayerNameMap = {}
    this.defaultLayerId = ''
    this.selectedLayerId = ''
    // 캐시 해제 없이 destroy만 수행 (destroy가 내부적으로 정리함)
    for (const entity of this.entities) {
      entity.node.destroy()
    }
    this.entities = []
    if (this.stage) {
      this.renderer.requestDraw()
    }
  }

  changeLayerOrder = (layerIds: string[]): void => {
    this.entityLayerMap = layerIds.reduce(
      (acc: Record<string, string[]>, layerId: string) => {
        acc[layerId] = this.entityLayerMap[layerId] ?? []
        return acc
      },
      {} as Record<string, string[]>,
    )
    this.renderer.drawNow()
  }

  // 시스템 레이어를 최상단으로 올림
  protected updateSystemLayer = (): void => {
    this.renderer.layers.system.moveToTop()
    this.renderer.layers.background.moveToBottom()
  }

  protected onLayerUpdate = (): void => {
    this.events.onUpdateLayer(
      Object.entries(this.softLayerNameMap).map(([layerId, layerName]: string[]) => ({
        layerId,
        layerName,
      })),
    )
  }
}
